import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";
import { TrackerSchema } from "../order-schema";
import { disputeEvidenceSchema_ } from "./dispute-evidence-definition";

export const disputeSource = ["shopify", "paypal", "stripe"] as const;

export type DisputeSource = (typeof disputeSource)[number];

export const disputeType = ["chargeback", "inquiry"] as const;

export type DisputeType = (typeof disputeType)[number];

export const disputeReason = [
  "bank_cannot_process",
  "credit_not_processed",
  "customer_initiated",
  "debit_not_authorized",
  "duplicate",
  "fraudulent",
  "general",
  "incorrect_account_details",
  "insufficient_funds",
  "noncompliant",
  "product_not_received",
  "product_unacceptable",
  "subscription_cancelled",
  "unrecognized",
  "product_not_as_described",
  "incorrect_amount",
  "payment_by_other_means",
  "bank_processing_error",
] as const;

export type DisputeReason = (typeof disputeReason)[number];

export const disputeStatus = [
  "needs_response",
  "under_review",
  "charge_refunded",
  "accepted",
  "won",
  "lost",
] as const;

export type DisputeStatus = (typeof disputeStatus)[number];

export const disputeSortableColumns = [
  "amount",
  "initiatedAt",
  "evidenceDueBy",
] as const;

export type DisputeSortableColumn = (typeof disputeSortableColumns)[number];

const disputeSortableColumnSet: ReadonlySet<string> = new Set(
  disputeSortableColumns,
);

export function isDisputeSortableColumn(
  value: string | undefined,
): value is DisputeSortableColumn {
  return value != null && disputeSortableColumnSet.has(value);
}

const disputeSchema_ = z.object({
  id: z.coerce.bigint(),
  accountId: z.coerce.bigint(),
  source: z.enum(disputeSource),
  sourceDisputeId: z.string(),
  sourceOrderId: z.string().nullish(),
  type: z.enum(disputeType),
  status: z.enum(disputeStatus),
  reason: z.enum(disputeReason).nullish(),
  networkReasonCode: z.string().nullish(),
  amount: z.coerce.number(),
  currency: z.string(),
  evidenceDueBy: z.coerce.date().nullish(),
  evidenceSentOn: z.coerce.date().nullish(),
  finalizedOn: z.coerce.date().nullish(),
  initiatedAt: z.coerce.date(),
  rawDispute: z.unknown(),
  aiEvidenceGeneratedAt: z.coerce.date().nullish(),
  autoEvidenceDisabled: z.boolean(),
  reviewed: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
});
export const disputeSchema: z.ZodType<Dispute> = disputeSchema_;
export interface Dispute extends z.infer<typeof disputeSchema_> {}

const fulfillmentTrackerSchema_ = z.object({
  _tracker: TrackerSchema,
  fulfillmentID: z.string().nullish(),
  locationId: z.string().nullish(),
});
export const fulfillmentTrackerSchema: z.ZodType<DisputeFulfillmentTracker> =
  fulfillmentTrackerSchema_;
export interface DisputeFulfillmentTracker
  extends z.infer<typeof fulfillmentTrackerSchema_> {}

const disputeViewSchema_ = disputeSchema_.extend({
  orderName: z.string().nullish(),
  customerName: z.string().nullish(),
  evidence: disputeEvidenceSchema_.nullish(),
  trackers: z.array(fulfillmentTrackerSchema_).nullish(),
  orderTrackingEnabled: z.boolean().nullish(),
  orderId: z.string().nullish(),
});

export const disputeViewSchema: z.ZodType<DisputeView> = disputeViewSchema_;
export interface DisputeView extends z.infer<typeof disputeViewSchema_> {}

// Timeline steps

export const disputeTimelineStep = [
  "DISPUTE_CREATED",
  "SUBMIT_EVIDENCE",
  "BANK_REVIEW",
  "DISPUTE_SETTLED",
] as const;

export type DisputeTimelineStep = (typeof disputeTimelineStep)[number];

export function getActiveTimelineStep(
  status: DisputeStatus,
): DisputeTimelineStep {
  switch (status) {
    case "needs_response":
      return "SUBMIT_EVIDENCE";
    case "under_review":
      return "BANK_REVIEW";
    case "won":
    case "lost":
    case "accepted":
    case "charge_refunded":
      return "DISPUTE_SETTLED";
    default:
      assertNever(status);
  }
}

export const REASON_ALIASES: Partial<Record<DisputeReason, DisputeReason>> = {
  product_unacceptable: "product_not_as_described",
  bank_cannot_process: "bank_processing_error",
  insufficient_funds: "bank_processing_error",
  incorrect_account_details: "bank_processing_error",
  debit_not_authorized: "bank_processing_error",
  noncompliant: "bank_processing_error",
  customer_initiated: "general",
};

export function resolveReason(reason: DisputeReason): DisputeReason {
  return REASON_ALIASES[reason] ?? reason;
}

export interface InsertDisputeInput {
  accountId: bigint;
  source: DisputeSource;
  sourceDisputeId: string;
  sourceOrderId?: string | null;
  type: DisputeType;
  status: DisputeStatus;
  reason?: DisputeReason | null;
  networkReasonCode?: string | null;
  amount: number;
  currency: string;
  evidenceDueBy?: Date | null;
  evidenceSentOn?: Date | null;
  finalizedOn?: Date | null;
  initiatedAt: Date;
  rawDispute: unknown;
}
